import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, TextField, Autocomplete,
    Tooltip, IconButton, Paper, Collapse, Select, MenuItem, Button, Chip,
    FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
    AddCircleOutline as AddForDayIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    AttachFile as AttachIcon,
    Close as CloseIcon,
    InsertDriveFile as FileIcon,
    BeachAccess as HolidayIcon,
    EventBusy as LeaveIcon,
    Work as WorkIcon,
    EventNote as ActivityIcon,
    Weekend as WeekendIcon,
} from '@mui/icons-material';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

export const DAY_COLORS = {
    Mon: '#6366f1', Tue: '#8b5cf6', Wed: '#06b6d4',
    Thu: '#10b981', Fri: '#f59e0b', Sat: '#ef4444', Sun: '#ef4444',
};

const LOCATIONS = ['Office', 'Remote', 'Client Site'];
const DAY_TYPE_CFG = {
    Working: { color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', label: 'Working', icon: WorkIcon },
    Activity: { color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', label: 'Activity', icon: ActivityIcon },
    Leave: { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', label: 'Leave', icon: LeaveIcon },
    Holiday: { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Holiday', icon: HolidayIcon },
    Weekend: { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', label: 'Weekend', icon: WeekendIcon },
};
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_MB = 10;

// ─── Entry row card ───────────────────────────────────────────────────────────
function EntryRow({ row, rowIndex, errors, projects, weekStart, weekEnd, onUpdate, onReplace, onDelete, isOnly, onSaveDraft, onAddForDate, isFirstForDate, userId, leaveTypes, maxDailyHours = 8, isMultiEntry = false, firstRowDayType, firstRowLeaveType }) {
    const [expanded, setExpanded] = useState(false);
    const [rowTasks, setRowTasks] = useState([]);
    const fileRef = useRef(null);
    const isWorking = row.dayType === 'Working' || (row.dayType === 'Leave' && row.leaveType === 'Work From Home');
    const isActivity = row.dayType === 'Activity';
    const isWorkLike = isWorking || isActivity;
    const isLeave    = row.dayType === 'Leave';
    const isHoliday  = row.dayType === 'Holiday';
    const isWeekend  = row.dayType === 'Weekend';
    const hasErrors  = !!errors[`${row.id}_hours`];
    const hasExtra   = (isWorking && !!row.description) || !!row.remarks ||
                       row.location !== 'Office' || row.attachments.length > 0;
    const rowError   = ['date', 'project', 'task', 'hours'].some((f) => errors[`${row.id}_${f}`]);
    const cfg       = DAY_TYPE_CFG[row.dayType] || DAY_TYPE_CFG.Working;
    const isL2Rejected = row.l2_status === 'REJECTED';
    const isL2Approved  = row.l2_status === 'APPROVED';

    // Approved rows stay fully editable, but the first edit in a session warns
    // the employee that changing it will require re-approval.
    const [approvedEditWarnOpen, setApprovedEditWarnOpen] = useState(false);
    const pendingApprovedEditRef = useRef(null);
    const approvedEditAckRef = useRef(false);

    const guardedUpdate = (id, field, value) => {
        if (isL2Approved && !approvedEditAckRef.current) {
            pendingApprovedEditRef.current = { kind: 'update', args: [id, field, value] };
            setApprovedEditWarnOpen(true);
            return;
        }
        onUpdate(id, field, value);
    };

    const guardedReplace = (id, updater) => {
        if (isL2Approved && !approvedEditAckRef.current) {
            pendingApprovedEditRef.current = { kind: 'replace', args: [id, updater] };
            setApprovedEditWarnOpen(true);
            return;
        }
        onReplace(id, updater);
    };

    const confirmApprovedEdit = () => {
        approvedEditAckRef.current = true;
        setApprovedEditWarnOpen(false);
        const pending = pendingApprovedEditRef.current;
        pendingApprovedEditRef.current = null;
        if (!pending) return;
        if (pending.kind === 'update') onUpdate(...pending.args);
        else onReplace(...pending.args);
    };

    const cancelApprovedEdit = () => {
        setApprovedEditWarnOpen(false);
        pendingApprovedEditRef.current = null;
    };

    useEffect(() => { if (hasErrors) setExpanded(true); }, [hasErrors]);

    useEffect(() => {
        let active = true;
        const fetchTasks = async () => {
            if (!row.project_id) {
                if (active) setRowTasks([]);
                return;
            }
            try {
                const endpoint = API_ENDPOINTS.getActiveTasksForEmployee(userId, row.project_id);
                const response = await getData(endpoint);
                if (response?.status === 'SUCCESS') {
                    if (active) setRowTasks(response.result || []);
                } else {
                    if (active) setRowTasks([]);
                }
            } catch (e) {
                console.error(e);
                if (active) setRowTasks([]);
            }
        };
        fetchTasks();
        return () => { active = false; };
    }, [row.project_id, userId]);

    const handleDayTypeChange = (val) => {
        // When adding a secondary row on a WFH day, 'Leave' must inherit 'Work From Home'
        const inheritedWFH = !isFirstForDate && val === 'Leave'
            && firstRowDayType === 'Leave' && firstRowLeaveType === 'Work From Home';

        guardedReplace(row.id, (r) => {
            let u = { ...r, dayType: val };
            if ((val === 'Leave' && !inheritedWFH) || val === 'Holiday' || val === 'Weekend') {
                u = { ...u, project: null, project_id: null, task: null, task_id: null, description: '', hours: '', overtime: '', location: null };
            }
            if (val === 'Activity') {
                u = { ...u, project: null, project_id: null, task: null, task_id: null };
                if (!r.location) u.location = 'Office';
            }
            if (val === 'Working' || inheritedWFH) {
                if (!r.location) u.location = 'Office';
            }
            if (inheritedWFH) {
                u.leaveType = 'Work From Home';
            } else {
                u.leaveType = val !== 'Leave' ? null : (leaveTypes[0] || null);
            }
            return u;
        });
        if ((val === 'Leave' && !inheritedWFH) || val === 'Holiday' || val === 'Weekend') setExpanded(false);
    };

    const handleFiles = (e) => {
        const valid = Array.from(e.target.files)
            .filter(f => ALLOWED_TYPES.includes(f.type) && f.size <= MAX_FILE_MB * 1024 * 1024)
            .map(f => ({ name: f.name, size: f.size, type: f.type, file: f }));
        guardedUpdate(row.id, 'attachments', [...row.attachments, ...valid]);
        e.target.value = '';
    };

    const openAttachment = (f) => {
        let url;
        if (f?.file) {
            url = URL.createObjectURL(f.file);
        } else if (typeof f === 'string') {
            url = f;
        } else if (f?.url) {
            url = f.url;
        }
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    const attachmentLabel = (f) => f.name || f.url || 'file';
    const attachmentTooltip = (f) => f.size ? `${attachmentLabel(f)} (${(f.size / 1024).toFixed(0)} KB)` : attachmentLabel(f);

    const inputSx = {
        '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
        '& .MuiInputBase-input': { fontSize: 13 },
    };

    return (
        <Paper elevation={0} sx={{
            border: isL2Rejected ? '1.5px solid #fca5a5' : isL2Approved ? '1.5px solid #86efac' : (rowError ? '1.5px solid #fca5a5' : '1px solid #e2e8f0'),
            borderRadius: 2.5, mb: 1.5, overflow: 'hidden',
            transition: 'box-shadow 0.2s, border-color 0.2s',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.07)', borderColor: isL2Rejected ? '#fca5a5' : isL2Approved ? '#86efac' : (rowError ? '#fca5a5' : '#c7d2fe') },
        }}>

            {/* ── Single main row: matches header column layout exactly ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                rowGap: 2, columnGap: 1, pl: 1.5, pr: 2, py: 1.25,
                bgcolor: isL2Rejected ? '#fff5f5' : isL2Approved ? '#f0fdf4' : '#fff',
                borderLeft: `4px solid ${isL2Rejected ? '#ef4444' : isL2Approved ? '#22c55e' : cfg.color}`,
            }}>
                {/* Row number — tooltip carries the external-approver rejection comment or approval note, no visible label */}
                <Tooltip
                    title={
                        isL2Rejected
                            ? (row.l2_comments ? `${row.l2ApproverName ? row.l2ApproverName + ': ' : ''}${row.l2_comments}` : 'Rejected by external approver')
                            : isL2Approved
                                ? `Approved${row.l2ApproverName ? ` by ${row.l2ApproverName}` : ' by external approver'}`
                                : ''
                    }
                    arrow
                    disableHoverListener={!isL2Rejected && !isL2Approved}
                >
                    <Typography sx={{
                        fontSize: 11, fontWeight: 700, minWidth: 18, flexShrink: 0,
                        color: isL2Rejected ? '#ef4444' : isL2Approved ? '#166534' : '#cbd5e1',
                        cursor: (isL2Rejected || isL2Approved) ? 'help' : 'default',
                    }}>
                        {String(rowIndex + 1).padStart(2, '0')}
                    </Typography>
                </Tooltip>

                {/* Date */}
                <Box sx={{ flex: { xs: '1 1 0%', sm: '0 0 auto' } }}>
                    <TextField
                        type="date" size="small" label="Date" value={row.date}
                        onChange={e => guardedUpdate(row.id, 'date', e.target.value)}
                        error={!!errors[`${row.id}_date`]}
                        helperText={errors[`${row.id}_date`]}
                        inputProps={{ min: weekStart, max: weekEnd || undefined }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 150 }, ...inputSx }}
                    />
                </Box>

                {/* Day Type — Select dropdown (first row for this date only; spacer for subsequent rows) */}
                {isFirstForDate ? (
                    <FormControl size="small" sx={{ width: { xs: '100%', sm: 145 }, flexShrink: 0 }}>
                        <InputLabel sx={{ fontSize: 12 }}>Day Type</InputLabel>
                        <Select
                            label="Day Type"
                            value={row.dayType}
                            onChange={e => handleDayTypeChange(e.target.value)}
                            renderValue={(v) => {
                                const c = DAY_TYPE_CFG[v];
                                const Icon = c.icon;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Icon sx={{ fontSize: 15, color: c.color }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.color }}>{c.label}</Typography>
                                    </Box>
                                );
                            }}
                            sx={{
                                borderRadius: 1.5,
                                color: cfg.color, bgcolor: cfg.bg,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: cfg.border },
                                '& .MuiSvgIcon-root': { color: cfg.color },
                            }}
                        >
                            {Object.entries(DAY_TYPE_CFG).map(([key, c]) => {
                                const Icon = c.icon;
                                return (
                                    <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Icon sx={{ fontSize: 15, color: c.color }} />
                                            <Typography sx={{ fontSize: 13, color: '#1e293b' }}>{c.label}</Typography>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                ) : (
                    <FormControl size="small" sx={{ width: { xs: '100%', sm: 145 }, flexShrink: 0 }}>
                        <InputLabel sx={{ fontSize: 12 }}>Day Type</InputLabel>
                        <Select
                            label="Day Type"
                            value={row.dayType}
                            onChange={e => handleDayTypeChange(e.target.value)}
                            renderValue={(v) => {
                                const c = DAY_TYPE_CFG[v];
                                const Icon = c.icon;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Icon sx={{ fontSize: 15, color: c.color }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.color }}>{c.label}</Typography>
                                    </Box>
                                );
                            }}
                            sx={{
                                borderRadius: 1.5,
                                color: cfg.color, bgcolor: cfg.bg,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: cfg.border },
                                '& .MuiSvgIcon-root': { color: cfg.color },
                            }}
                        >
                            {(() => {
                                // Determine allowed types for additional rows on the same day
                                const firstIsWFH = firstRowDayType === 'Leave' && firstRowLeaveType === 'Work From Home';
                                const allowedKeys = firstIsWFH ? ['Leave', 'Activity'] : ['Working', 'Activity'];
                                return allowedKeys.map((key) => {
                                    const c = DAY_TYPE_CFG[key];
                                    const Icon = c.icon;
                                    return (
                                        <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Icon sx={{ fontSize: 15, color: c.color }} />
                                                <Typography sx={{ fontSize: 13, color: '#1e293b' }}>{c.label}</Typography>
                                            </Box>
                                        </MenuItem>
                                    );
                                });
                            })()}
                        </Select>
                    </FormControl>
                )}

                {/* Content area — flex:1 so Task fills remaining space before Actions */}
                <Box sx={{ display: 'flex', rowGap: 2, columnGap: 1, flexWrap: 'wrap', alignItems: 'center', flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: 0 }}>

                    {isWorking && (
                        <>
                            {/* Project */}
                            <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
                                <Autocomplete
                                    size="small"
                                    options={projects}
                                    value={projects.find(p => p.id === row.project_id) || null}
                                    onChange={(_, v) => {
                                        guardedUpdate(row.id, 'project',    v?.project_title || null);
                                        guardedUpdate(row.id, 'project_id', v?.id || 0);
                                        guardedUpdate(row.id, 'task',       null);
                                        guardedUpdate(row.id, 'task_id',    0);
                                    }}
                                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                                    getOptionLabel={(opt) => opt?.project_title || ''}
                                    renderInput={params => (
                                        <TextField {...params} label="Project"
                                            error={!!errors[`${row.id}_project`]}
                                            helperText={errors[`${row.id}_project`]}
                                            sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 13 } }}
                                        />
                                    )}
                                    renderOption={(props, opt) => (
                                        <li {...props} key={opt.id}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                                                {opt.project_title}
                                            </Typography>
                                        </li>
                                    )}
                                />
                            </Box>

                            {/* Task */}
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 180, flexShrink: 1 }}>
                                <Autocomplete
                                    size="small"
                                    options={rowTasks}
                                    value={rowTasks.find(t => t.id === row.task_id) || null}
                                    onChange={(_, v) => {
                                        guardedUpdate(row.id, 'task',    v?.task_title || null);
                                        guardedUpdate(row.id, 'task_id', v?.id || 0);
                                    }}
                                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                                    getOptionLabel={(opt) => opt?.task_title || ''}
                                    renderInput={params => (
                                        <TextField {...params} label="Task"
                                            error={!!errors[`${row.id}_task`]}
                                            helperText={errors[`${row.id}_task`]}
                                            sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: 13 } }}
                                        />
                                    )}
                                    renderOption={(props, opt) => (
                                        <li {...props} key={opt.id}>
                                            <Typography sx={{ fontSize: 13 }}>{opt.task_title}</Typography>
                                        </li>
                                    )}
                                />
                            </Box>

                            {/* Hours */}
                            <TextField
                                size="small" type="number" label="Hours"
                                value={row.hours}
                                onChange={e => {
                                    const raw = e.target.value;
                                    if (raw === '') { guardedUpdate(row.id, 'hours', ''); return; }
                                    const num = parseFloat(raw);
                                    if (!isNaN(num))
                                        guardedUpdate(row.id, 'hours', Math.min(maxDailyHours, Math.max(0, num)));
                                }}
                                error={!!errors[`${row.id}_hours`]}
                                helperText={
                                    errors[`${row.id}_hours`] ||
                                    (!isMultiEntry && Number(row.hours) >= maxDailyHours && maxDailyHours > 0
                                        ? `Max ${maxDailyHours} hrs — add extra time as OT`
                                        : null)
                                }
                                FormHelperTextProps={{
                                    sx: !errors[`${row.id}_hours`] && !isMultiEntry && Number(row.hours) >= maxDailyHours
                                        ? { color: '#d97706', fontSize: 10, whiteSpace: 'nowrap', mx: 0.3, mt: 0.5 }
                                        : { fontSize: 10 },
                                }}
                                inputProps={{ min: 0, max: maxDailyHours, step: 0.25 }}
                                sx={{
                                    width: { xs: '100%', sm: 80 }, flexShrink: 0, ...inputSx,
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                            />
                        </>
                    )}

                    {/* Activity — free-text description spans project + task columns */}
                    {isActivity && (
                        <>
                            <TextField
                                size="small"
                                label="Activity Description"
                                value={row.description}
                                onChange={e => guardedUpdate(row.id, 'description', e.target.value)}
                                error={!!errors[`${row.id}_description`]}
                                helperText={errors[`${row.id}_description`]}
                                inputProps={{ maxLength: 300 }}
                                sx={{ flex: '3 1 330px', minWidth: 250, ...inputSx }}
                            />
                            {/* Hours */}
                            <TextField
                                size="small" type="number" label="Hours"
                                value={row.hours}
                                onChange={e => {
                                    const raw = e.target.value;
                                    if (raw === '') { guardedUpdate(row.id, 'hours', ''); return; }
                                    const num = parseFloat(raw);
                                    if (!isNaN(num))
                                        guardedUpdate(row.id, 'hours', Math.min(maxDailyHours, Math.max(0, num)));
                                }}
                                error={!!errors[`${row.id}_hours`]}
                                helperText={
                                    errors[`${row.id}_hours`] ||
                                    (!isMultiEntry && Number(row.hours) >= maxDailyHours && maxDailyHours > 0
                                        ? `Max ${maxDailyHours} hrs — add extra time as OT`
                                        : null)
                                }
                                FormHelperTextProps={{
                                    sx: !errors[`${row.id}_hours`] && !isMultiEntry && Number(row.hours) >= maxDailyHours
                                        ? { color: '#d97706', fontSize: 10, whiteSpace: 'nowrap', mx: 0.3, mt: 0.5 }
                                        : { fontSize: 10 },
                                }}
                                inputProps={{ min: 0, max: maxDailyHours, step: 0.25 }}
                                sx={{
                                    width: { xs: '100%', sm: 80 }, flexShrink: 0, ...inputSx,
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                            />
                        </>
                    )}

                    {/* Leave type selector */}
                    {isFirstForDate && isLeave && (
                        <FormControl size="small" sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}>
                            <InputLabel sx={{ fontSize: 12 }}>Leave Type</InputLabel>
                            <Select
                                label="Leave Type"
                                value={row.leaveType || ''}
                                onChange={e => guardedUpdate(row.id, 'leaveType', e.target.value)}
                                sx={{
                                    fontSize: 13, borderRadius: 1.5,
                                    bgcolor: '#f5f3ff', color: '#7c3aed',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd6fe' },
                                    '& .MuiSvgIcon-root': { color: '#7c3aed' },
                                }}
                            >
                                {leaveTypes.map(t => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}

                    {/* Holiday badge */}
                    {isFirstForDate && isHoliday && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 0.6, borderRadius: 1.5,
                            bgcolor: '#fffbeb', border: '1px solid #fde68a',
                        }}>
                            <HolidayIcon sx={{ fontSize: 15, color: '#d97706' }} />
                            <Typography sx={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>Public Holiday</Typography>
                            <Typography sx={{ fontSize: 12, color: '#b45309' }}>— no work entry required</Typography>
                        </Box>
                    )}

                    {/* Weekend badge */}
                    {isFirstForDate && isWeekend && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 0.6, borderRadius: 1.5,
                            bgcolor: '#f1f5f9', border: '1px solid #cbd5e1',
                        }}>
                            <WeekendIcon sx={{ fontSize: 15, color: '#64748b' }} />
                            <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>Weekend</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>— no work entry required</Typography>
                        </Box>
                    )}
                </Box>

                {/* Actions column — attach icon + expand/save/delete/add */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {isWorkLike && (
                        <Tooltip title={row.attachments.length > 0 ? `${row.attachments.length} file(s) attached` : 'Attach files'}>
                            <Box sx={{ position: 'relative' }}>
                                <IconButton size="small" onClick={() => fileRef.current?.click()}
                                    sx={{ color: row.attachments.length > 0 ? cfg.color : '#94a3b8', '&:hover': { bgcolor: cfg.bg } }}>
                                    <AttachIcon fontSize="small" />
                                </IconButton>
                                {row.attachments.length > 0 && (
                                    <Box sx={{
                                        position: 'absolute', top: 0, right: 0,
                                        width: 14, height: 14, borderRadius: '50%',
                                        bgcolor: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Typography sx={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>
                                            {row.attachments.length}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Tooltip>
                    )}
                    {isWorkLike && (
                        <Tooltip title={expanded ? 'Collapse details' : isActivity ? 'Add location & more' : 'Add description & more'}>
                            <IconButton size="small" onClick={() => setExpanded(p => !p)}
                                sx={{
                                    color: (hasExtra || hasErrors) ? cfg.color : '#94a3b8',
                                    bgcolor: (hasExtra || hasErrors) ? cfg.bg : 'transparent',
                                    '&:hover': { bgcolor: cfg.bg, color: cfg.color },
                                }}>
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}

                    {isFirstForDate && isLeave && !isWorkLike && (
                        <Tooltip title={expanded ? 'Hide remarks' : 'Add remarks'}>
                            <IconButton size="small" onClick={() => setExpanded(p => !p)}
                                sx={{ color: row.remarks ? '#7c3aed' : '#94a3b8', '&:hover': { bgcolor: '#f5f3ff' } }}>
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}

                    {!!(row.date || row.project || row.task || row.hours || row.description || row.remarks) && (
                        <Tooltip title="Save as Draft" arrow>
                            <IconButton size="small" onClick={onSaveDraft}
                                sx={{
                                    color: '#6366f1',
                                    '&:hover': { bgcolor: '#e0e7ff', borderColor: '#a5b4fc' },
                                }}>
                                <SaveIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="Remove entry">
                        <span>
                            <IconButton size="small" onClick={() => onDelete(row.id)} disabled={isOnly}
                                sx={{ color: '#ef4444', opacity: isOnly ? 0.25 : 1, '&:hover': { bgcolor: '#fef2f2' } }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {isWorkLike && row.date ? (
                        isFirstForDate ? (
                            <Tooltip title="Add another entry for this day" arrow>
                                <IconButton size="small" onClick={() => onAddForDate(row.date, row.day, row.dayType, row.leaveType)}
                                    sx={{ color: cfg.color, '&:hover': { bgcolor: cfg.bg } }}>
                                    <AddForDayIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Box sx={{ width: 34, flexShrink: 0 }} />
                        )
                    ) : null}
                </Box>
            </Box>

            <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFiles} />

            {/* ── Expanded: Working details ── */}
            {isWorking && (
                <Collapse in={expanded}>
                    <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, bgcolor: '#fafbff', borderTop: '1px dashed #e2e8f0' }}>
                        {/* Location | OT | Description | Remarks */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Location</Typography>
                                <Select size="small" fullWidth value={row.location || 'Office'}
                                    onChange={e => guardedUpdate(row.id, 'location', e.target.value)}
                                    sx={{ fontSize: 13, borderRadius: 1.5, bgcolor: '#fff' }}>
                                    {LOCATIONS.map(l => <MenuItem key={l} value={l} sx={{ fontSize: 13 }}>{l}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 110px' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Overtime Hrs</Typography>
                                <TextField
                                    size="small" type="number" placeholder="0"
                                    value={row.overtime}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') { guardedUpdate(row.id, 'overtime', ''); return; }
                                        const num = parseFloat(raw);
                                        if (!isNaN(num))
                                            guardedUpdate(row.id, 'overtime', Math.min(8, Math.max(0, num)));
                                    }}
                                    error={!!errors[`${row.id}_overtime`]}
                                    helperText={errors[`${row.id}_overtime`]}
                                    inputProps={{ min: 0, max: 8, step: 0.25 }}
                                    InputProps={{ endAdornment: <Typography sx={{ fontSize: 10, color: '#f59e0b', mr: 0.5, fontWeight: 700 }}>OT</Typography> }}
                                    sx={{ width: '100%', ...inputSx }}
                                />
                            </Box>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>
                                    Work Description
                                </Typography>
                                <TextField fullWidth size="small" multiline minRows={1}
                                    placeholder="Describe the work done"
                                    value={row.description}
                                    onChange={e => guardedUpdate(row.id, 'description', e.target.value)}
                                    error={!!errors[`${row.id}_description`]}
                                    helperText={errors[`${row.id}_description`]}
                                    inputProps={{ maxLength: 500 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13, bgcolor: '#fff' } }}
                                />
                            </Box>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Remarks</Typography>
                                <TextField fullWidth size="small" multiline minRows={1}
                                    placeholder="Optional notes…"
                                    value={row.remarks}
                                    onChange={e => guardedUpdate(row.id, 'remarks', e.target.value)}
                                    inputProps={{ maxLength: 250 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13, bgcolor: '#fff' } }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: row.attachments.length > 0 ? 1 : 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Attachments</Typography>
                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>PDF · JPG · PNG · max 10 MB each</Typography>
                                <Button size="small" startIcon={<AttachIcon sx={{ fontSize: '13px !important' }} />}
                                    onClick={() => fileRef.current?.click()}
                                    sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#6366f1', py: 0.25, px: { xs: 0.75, sm: 1 }, minWidth: 'auto', borderRadius: 1.5, '&:hover': { bgcolor: '#eef2ff' }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } } }}>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add file</Box>
                                </Button>
                            </Box>
                            {row.attachments.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {row.attachments.map((f, i) => (
                                        <Chip key={i}
                                            icon={<FileIcon sx={{ fontSize: '14px !important', color: '#6366f1 !important' }} />}
                                            label={
                                                <Tooltip title={attachmentTooltip(f)}>
                                                    <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                                        {attachmentLabel(f)}
                                                    </span>
                                                </Tooltip>
                                            }
                                            onClick={() => openAttachment(f)}
                                            onDelete={() => guardedUpdate(row.id, 'attachments', row.attachments.filter((_, j) => j !== i))}
                                            deleteIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                            size="small"
                                            sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 500, fontSize: 11, borderRadius: 1.5, border: '1px solid #c7d2fe', cursor: 'pointer', '& .MuiChip-deleteIcon': { color: '#a5b4fc' } }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            )}

            {/* ── Expanded: Activity details (location · overtime · remarks · attachments) ── */}
            {isActivity && (
                <Collapse in={expanded}>
                    <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, bgcolor: '#f0fdfe', borderTop: '1px dashed #a5f3fc' }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Location</Typography>
                                <Select size="small" fullWidth value={row.location || 'Office'}
                                    onChange={e => guardedUpdate(row.id, 'location', e.target.value)}
                                    sx={{ fontSize: 13, borderRadius: 1.5, bgcolor: '#fff' }}>
                                    {LOCATIONS.map(l => <MenuItem key={l} value={l} sx={{ fontSize: 13 }}>{l}</MenuItem>)}
                                </Select>
                            </Box>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 110px' } }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Overtime Hrs</Typography>
                                <TextField
                                    size="small" type="number" placeholder="0"
                                    value={row.overtime}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') { guardedUpdate(row.id, 'overtime', ''); return; }
                                        const num = parseFloat(raw);
                                        if (!isNaN(num))
                                            guardedUpdate(row.id, 'overtime', Math.min(8, Math.max(0, num)));
                                    }}
                                    error={!!errors[`${row.id}_overtime`]}
                                    helperText={errors[`${row.id}_overtime`]}
                                    inputProps={{ min: 0, max: 8, step: 0.25 }}
                                    InputProps={{ endAdornment: <Typography sx={{ fontSize: 10, color: '#f59e0b', mr: 0.5, fontWeight: 700 }}>OT</Typography> }}
                                    sx={{ width: '100%', ...inputSx }}
                                />
                            </Box>
                            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Remarks</Typography>
                                <TextField fullWidth size="small" multiline minRows={1}
                                    placeholder="Optional notes…"
                                    value={row.remarks}
                                    onChange={e => guardedUpdate(row.id, 'remarks', e.target.value)}
                                    inputProps={{ maxLength: 250 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13, bgcolor: '#fff' } }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: row.attachments.length > 0 ? 1 : 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Attachments</Typography>
                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>PDF · JPG · PNG · max 10 MB each</Typography>
                                <Button size="small" startIcon={<AttachIcon sx={{ fontSize: '13px !important' }} />}
                                    onClick={() => fileRef.current?.click()}
                                    sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#0891b2', py: 0.25, px: { xs: 0.75, sm: 1 }, minWidth: 'auto', borderRadius: 1.5, '&:hover': { bgcolor: '#ecfeff' }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } } }}>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add file</Box>
                                </Button>
                            </Box>
                            {row.attachments.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {row.attachments.map((f, i) => (
                                        <Chip key={i}
                                            icon={<FileIcon sx={{ fontSize: '14px !important', color: '#0891b2 !important' }} />}
                                            label={
                                                <Tooltip title={attachmentTooltip(f)}>
                                                    <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                                        {attachmentLabel(f)}
                                                    </span>
                                                </Tooltip>
                                            }
                                            onClick={() => openAttachment(f)}
                                            onDelete={() => guardedUpdate(row.id, 'attachments', row.attachments.filter((_, j) => j !== i))}
                                            deleteIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                            size="small"
                                            sx={{ bgcolor: '#ecfeff', color: '#0e7490', fontWeight: 500, fontSize: 11, borderRadius: 1.5, border: '1px solid #a5f3fc', cursor: 'pointer', '& .MuiChip-deleteIcon': { color: '#67e8f9' } }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            )}

            {/* ── Expanded: Leave remarks ── */}
            {isLeave && !isWorkLike && (
                <Collapse in={expanded}>
                    <Box sx={{ px: 2.5, pb: 2, pt: 1.5, bgcolor: '#faf5ff', borderTop: '1px dashed #ddd6fe' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', mb: 0.5 }}>Remarks (optional)</Typography>
                        <TextField fullWidth size="small"
                            placeholder="Reason or notes for this leave day…"
                            value={row.remarks}
                            onChange={e => guardedUpdate(row.id, 'remarks', e.target.value)}
                            inputProps={{ maxLength: 250 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: 13 } }}
                        />
                    </Box>
                </Collapse>
            )}

            {/* ── Warn before editing an already-approved entry ── */}
            <Dialog open={approvedEditWarnOpen} onClose={cancelApprovedEdit} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit approved entry?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: '#475569' }}>
                        This entry was already approved by the external approver. Editing it will require re-approval before it can be processed. Do you want to continue?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={cancelApprovedEdit} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={confirmApprovedEdit} variant="contained"
                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', boxShadow: 'none' }}>
                        Continue Editing
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default EntryRow;
